import * as React from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { XIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { useCardStyle } from "@/features/theme/contexts/ThemeContext"

function Sheet({
  open,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Root>) {
  // Track previous open state to detect transitions
  const prevOpenRef = React.useRef(open)

  // Fix: Blur focused element when sheet opens via controlled prop
  // This prevents aria-hidden conflict with elements in #root
  // See: https://github.com/radix-ui/primitives/issues/1241
  React.useEffect(() => {
    const wasOpen = prevOpenRef.current
    prevOpenRef.current = open

    if (open && !wasOpen) {
      // Sheet is opening - blur any focused element in #root
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    }

    if (!open && wasOpen) {
      // Sheet is closing - force cleanup aria-hidden
      // Use timeout to run after Radix cleanup
      const timeoutId = setTimeout(() => {
        const root = document.getElementById('root')
        if (root?.hasAttribute('aria-hidden')) {
          root.removeAttribute('aria-hidden')
        }
      }, 100)
      return () => clearTimeout(timeoutId)
    }
  }, [open])

  const handleOpenChange = React.useCallback(
    (newOpen: boolean) => {
      // Blur any focused element to prevent aria-hidden conflicts
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
      onOpenChange?.(newOpen)
      // When closing, force remove aria-hidden from #root to prevent page lock
      if (!newOpen) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const root = document.getElementById('root')
            if (root?.hasAttribute('aria-hidden')) {
              root.removeAttribute('aria-hidden')
            }
          })
        })
      }
    },
    [onOpenChange]
  )

  return <SheetPrimitive.Root data-slot="sheet" open={open} onOpenChange={handleOpenChange} {...props} />
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />
}

function SheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
        className
      )}
      {...props}
    />
  )
}

function SheetContent({
  className,
  children,
  side = "right",
  style,
  onOpenAutoFocus,
  onCloseAutoFocus,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "top" | "right" | "bottom" | "left"
}) {
  const { popoverStyleObject, borderClassName } = useCardStyle();
  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        className={cn(
          "data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-4 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
          side === "right" &&
            cn("data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm", borderClassName),
          side === "left" &&
            cn("data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm", borderClassName),
          side === "top" &&
            cn("data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto border-b", borderClassName),
          side === "bottom" &&
            cn("data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto border-t", borderClassName),
          className
        )}
        style={{ ...popoverStyleObject, ...style }}
        // Fix: Prevent aria-hidden conflict when sheet opens
        // Delay focus to ensure Portal is fully mounted and aria-hidden is properly set up
        // See: https://github.com/radix-ui/primitives/issues/1241
        onOpenAutoFocus={(e) => {
          // Prevent immediate focus which can conflict with aria-hidden on #root
          e.preventDefault()
          // Use requestAnimationFrame to delay focus until after Portal is mounted
          requestAnimationFrame(() => {
            // Focus the sheet content itself or let the focus trap handle it
            const content = e.currentTarget
            if (content instanceof HTMLElement) {
              content.focus()
            }
          })
          // Call custom handler if provided
          onOpenAutoFocus?.(e)
        }}
        // Fix: Prevent aria-hidden from getting stuck on #root when sheet closes
        // This happens when focus is still inside the sheet during unmount
        // See: https://github.com/radix-ui/primitives/issues/1241
        onCloseAutoFocus={(e) => {
          // Prevent default focus restoration which can conflict with nested Radix components (Select, etc.)
          e.preventDefault()
          // Blur any focused element to ensure nothing retains focus during unmount
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur()
          }
          // Force remove aria-hidden from #root to prevent page lock
          const root = document.getElementById('root')
          if (root?.hasAttribute('aria-hidden')) {
            root.removeAttribute('aria-hidden')
          }
          // Call custom handler if provided
          onCloseAutoFocus?.(e)
        }}
        {...props}
      >
        {children}
        <SheetPrimitive.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none">
          <XIcon className="size-4" />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </SheetPortal>
  )
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn("flex flex-col gap-1.5 p-4", className)}
      {...props}
    />
  )
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn("mt-auto flex flex-col gap-2 p-4", className)}
      {...props}
    />
  )
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn("text-foreground font-semibold", className)}
      {...props}
    />
  )
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  )
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
